import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { getContacts, createContact, updateContact, deleteContact, importContacts, bulkDeleteContacts, bulkUpdateContactGroups, createGroup } from '../lib/api';
import { getListCache, setListCache, clearListCache } from '../lib/listCache';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Upload, Search, Edit, Trash2, Loader2, Filter, AlertCircle, Users } from 'lucide-react';

export default function ContactsPage() {
  const { canEdit } = useAuth();
  // grupo pré-selecionado via ?group=<id> (vindo da tela Grupos → "ver contatos")
  const [searchParams] = useSearchParams();
  const _initGroup = searchParams.get('group') || '';
  // Cache stale-while-revalidate: se já visitamos com estes filtros, começa com os dados
  // do cache (sem loader) e revalida em segundo plano — some o "carregando" a cada visita.
  const _initCache = getListCache('contacts:' + JSON.stringify({ page: 0, size: 20, search: undefined, group_id: _initGroup || undefined }));
  const [contacts, setContacts] = useState(_initCache?.value.data || []);
  const [total, setTotal] = useState(_initCache?.value.total || 0);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const [groupFilter, setGroupFilter] = useState(_initGroup);
  const [availableGroups, setAvailableGroups] = useState([]);
  const [loading, setLoading] = useState(!_initCache);
  const [loadingPercent, setLoadingPercent] = useState(0);
  const percentTimerRef = useRef(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [formData, setFormData] = useState({ name: '', phone: '', group_ids: [] });
  const [submitting, setSubmitting] = useState(false);
  const [importResultOpen, setImportResultOpen] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [selectedImportGroups, setSelectedImportGroups] = useState([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  // Wizard de importação: 1 instruções · 2 arquivo · 3 conferir (prévia) · 4 grupos
  const [importStep, setImportStep] = useState(1);
  const [importPreview, setImportPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [templateLoading, setTemplateLoading] = useState(false);
  const [importDragOver, setImportDragOver] = useState(false);
  const [contactPhoneStatus, setContactPhoneStatus] = useState(null);
  const [contactToDelete, setContactToDelete] = useState(null);
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [groupDialogOpen, setGroupDialogOpen] = useState(false);
  const [groupAction, setGroupAction] = useState('add'); // 'add' | 'remove'
  const [groupDialogSelected, setGroupDialogSelected] = useState([]);
  const [applyingGroups, setApplyingGroups] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [creatingGroup, setCreatingGroup] = useState(false);
  const [selectAllMatching, setSelectAllMatching] = useState(false);
  const fileInputRef = useRef(null);

  // Efeito para animação de loading
  useEffect(() => {
    if (loading) {
      setLoadingPercent(0);
      percentTimerRef.current = setInterval(() => {
        setLoadingPercent(prev => {
          if (prev >= 95) return 95;
          const increment = Math.max(0.5, (95 - prev) / 20);
          return Math.min(95, prev + increment);
        });
      }, 100);
    } else {
      setLoadingPercent(100);
      if (percentTimerRef.current) {
        clearInterval(percentTimerRef.current);
        percentTimerRef.current = null;
      }
    }
    return () => {
      if (percentTimerRef.current) clearInterval(percentTimerRef.current);
    };
  }, [loading]);

  useEffect(() => {
    fetchContacts();
  }, [page, search, groupFilter]);

  useEffect(() => {
    fetchAvailableGroups();
  }, []);

  const fetchAvailableGroups = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/groups`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setAvailableGroups(data);
    } catch (error) {
      console.error('Erro ao carregar grupos:', error);
    }
  };

  const fetchContacts = async ({ background = false } = {}) => {
    const params = { page, size: 20, search: search || undefined, group_id: groupFilter || undefined };
    const cacheKey = 'contacts:' + JSON.stringify(params);
    if (!background) {
      const cached = getListCache(cacheKey);
      if (cached) {
        setContacts(cached.value.data);
        setTotal(cached.value.total);
        setSelectedContacts([]);
        setSelectAllMatching(false);
        setLoading(false);
        fetchContacts({ background: true }); // revalida em segundo plano (sem loader)
        return;
      }
    }
    try {
      const response = await getContacts(params);
      setContacts(response.data.data);
      setTotal(response.data.total);
      if (!background) {
        setSelectedContacts([]);
        setSelectAllMatching(false);
      }
      setListCache(cacheKey, { data: response.data.data, total: response.data.total });
    } catch (error) {
      if (!background) toast.error('Erro ao carregar contatos');
    } finally {
      if (!background) setLoading(false);
    }
  };

  const handleOpenDialog = (contact = null) => {
    if (contact) {
      setEditingContact(contact);
      setFormData({
        name: contact.name,
        phone: contact.phone,
        group_ids: contact.group_ids || [],
      });
    } else {
      setEditingContact(null);
      setFormData({ name: '', phone: '', group_ids: [] });
    }
    setContactPhoneStatus(null);
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Valida só o formato do número (não verificamos mais existência no WhatsApp).
    const cleaned = formData.phone?.replace(/[\s\-()]/g, '') || '';
    if (cleaned) {
      const phoneRegex = /^\+\d{10,15}$/;
      if (!phoneRegex.test(cleaned)) {
        setContactPhoneStatus('format_error');
        toast.error('Número inválido. Use o formato internacional: +5521999999999');
        return;
      }
      setContactPhoneStatus(null);
    }

    setSubmitting(true);

    try {
      const data = {
        name: formData.name,
        phone: formData.phone,
        group_ids: formData.group_ids,
      };

      if (editingContact) {
        await updateContact(editingContact._id, data);
        toast.success('Contato atualizado');
      } else {
        await createContact(data);
        toast.success('Contato criado');
      }

      setDialogOpen(false);
      clearListCache(); // mutação → invalida caches de lista (contatos + grupos)
      fetchContacts();
      fetchAvailableGroups(); // Atualizar lista de grupos disponíveis
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      const statusCode = error.response?.status;
      
      // Show modal for duplicate phone error (409), toast for other errors
      if (statusCode === 409 && errorDetail) {
        setErrorMessage(errorDetail);
        setErrorModalOpen(true);
      } else {
        toast.error(errorDetail || 'Erro ao salvar contato');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = (contact) => {
    setContactToDelete(contact);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!contactToDelete) return;

    try {
      await deleteContact(contactToDelete._id);
      toast.success('Contato excluído');
      setDeleteDialogOpen(false);
      setContactToDelete(null);
      clearListCache();
      fetchContacts();
    } catch (error) {
      toast.error('Erro ao excluir contato');
    }
  };

  const toggleSelectContact = (id) => {
    // mexer na seleção manual quebra o "todos do filtro"
    setSelectAllMatching(false);
    setSelectedContacts(prev =>
      prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    setSelectAllMatching(false);
    if (selectedContacts.length === contacts.length) {
      setSelectedContacts([]);
    } else {
      setSelectedContacts(contacts.map(c => c._id));
    }
  };

  // Alvo das ações em massa: ou TODO o conjunto filtrado (all+filter) ou os ids da seleção.
  const currentFilter = () => ({ search: search || undefined, group_id: groupFilter || undefined });
  const bulkTarget = () => (selectAllMatching ? { all: true, filter: currentFilter() } : { ids: selectedContacts });

  const confirmBulkDelete = async () => {
    setBulkDeleting(true);
    try {
      const res = await bulkDeleteContacts(bulkTarget());
      toast.success(`${res.data.deleted} contato(s) excluído(s)`);
      setBulkDeleteDialogOpen(false);
      setSelectedContacts([]);
      clearListCache();
      fetchContacts();
    } catch (error) {
      toast.error('Erro ao excluir contatos');
    } finally {
      setBulkDeleting(false);
    }
  };

  const openGroupDialog = () => {
    setGroupAction('add');
    setGroupDialogSelected([]);
    setNewGroupName('');
    setGroupDialogOpen(true);
  };

  const toggleGroupDialogSelected = (id) => {
    setGroupDialogSelected(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  // Troca de aba (adicionar/remover) limpa a marcação: evita aplicar um grupo
  // que ficou escondido na outra aba.
  const changeGroupAction = (action) => {
    if (action === groupAction) return;
    setGroupAction(action);
    setGroupDialogSelected([]);
  };

  // Filtra os grupos exibidos no diálogo pela pertinência dos contatos selecionados,
  // para não oferecer opções sem efeito (adicionar grupo que já tem / remover grupo
  // que não tem). No modo "todos do filtro" não conhecemos os grupos de todos os N,
  // então caímos no fallback: mostra a lista completa.
  const groupsForDialog = () => {
    if (selectAllMatching) return availableGroups;
    const selectedObjs = contacts.filter(c => selectedContacts.includes(c._id));
    if (selectedObjs.length === 0) return availableGroups;
    if (groupAction === 'add') {
      // grupos onde ao menos um selecionado ainda NÃO está (há o que adicionar)
      return availableGroups.filter(g =>
        selectedObjs.some(c => !(c.group_ids || []).includes(g._id))
      );
    }
    // remover: grupos onde ao menos um selecionado ESTÁ (há o que remover)
    return availableGroups.filter(g =>
      selectedObjs.some(c => (c.group_ids || []).includes(g._id))
    );
  };

  const handleCreateGroupInline = async () => {
    const name = newGroupName.trim();
    if (!name) return;
    setCreatingGroup(true);
    try {
      const res = await createGroup(name);
      const g = res.data;
      clearListCache(); // grupo novo → invalida caches de lista
      await fetchAvailableGroups();
      // já deixa o grupo recém-criado marcado para aplicar
      setGroupDialogSelected(prev => (prev.includes(g._id) ? prev : [...prev, g._id]));
      setNewGroupName('');
      toast.success(`Grupo "${name}" criado`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar grupo');
    } finally {
      setCreatingGroup(false);
    }
  };

  const applyGroups = async () => {
    if (groupDialogSelected.length === 0) return;
    setApplyingGroups(true);
    try {
      const res = await bulkUpdateContactGroups({ ...bulkTarget(), group_ids: groupDialogSelected, action: groupAction });
      const n = res.data?.modified ?? 0;
      toast.success(
        groupAction === 'add'
          ? `${n} contato(s) adicionado(s) ao(s) grupo(s)`
          : `${n} contato(s) removido(s) do(s) grupo(s)`
      );
      setGroupDialogOpen(false);
      setGroupDialogSelected([]);
      clearListCache();
      fetchContacts();
      fetchAvailableGroups();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar grupos');
    } finally {
      setApplyingGroups(false);
    }
  };

  // Baixa o modelo em .xlsx, gerado no backend. É Excel e não CSV de propósito: ao abrir
  // um .csv o Excel adivinha o tipo da célula e transforma 21988887777 em 2,19889E+10,
  // gravando de volta o que está exibido. No .xlsx o tipo vem gravado no arquivo e a
  // coluna do telefone já vem como Texto, então o número sobrevive.
  const downloadTemplate = async () => {
    setTemplateLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contacts/import-template`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!res.ok) {
        toast.error('Não foi possível baixar o modelo.');
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'modelo-contatos-watizap.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Não foi possível baixar o modelo.');
    } finally {
      setTemplateLoading(false);
    }
  };

  const resetImportWizard = () => {
    setImportStep(1);
    setImportFile(null);
    setImportPreview(null);
    setSelectedImportGroups([]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Prévia (dry-run): mesma rota /import, mas sem gravar — devolve a classificação por linha.
  const runImportPreview = async (file) => {
    if (!file) return;
    setPreviewLoading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('dry_run', 'true');
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contacts/import`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: fd,
      });
      const result = await response.json();
      if (!response.ok) {
        // Backend recusou o arquivo inteiro (.xls antigo, planilha sem coluna de
        // telefone, etc). A mensagem dele ensina a sair, então mostro ela.
        toast.error(result?.detail || 'Não foi possível ler o arquivo.');
        setImportFile(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
        return;
      }
      setImportPreview(result);
      setImportStep(3);
    } catch (error) {
      toast.error('Erro ao ler o arquivo');
    } finally {
      setPreviewLoading(false);
    }
  };

  const onImportFileChosen = (file) => {
    if (!file) return;
    setImportFile(file);
    runImportPreview(file);
  };

  const handleImport = async () => {
    if (!importFile) return;

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      formData.append('group_ids', selectedImportGroups.join(','));
      
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contacts/import`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      const result = await response.json();
      const { imported, skipped, errors, skipped_contacts } = result;
      
      // Store result for modal
      setImportResult({
        imported,
        skipped, 
        errors,
        skipped_contacts
      });
      
      // Show success toast for imported contacts
      if (imported > 0) {
        toast.success(`${imported} contato(s) importado(s) com sucesso!`);
      }
      
      // Show errors as toast
      if (errors?.length > 0) {
        toast.error(`${errors.length} linha(s) com erro: ${errors[0]}`);
      }
      
      // Show modal if there are skipped contacts or if nothing was processed
      if (skipped > 0 || (imported === 0 && skipped === 0 && errors?.length === 0)) {
        setImportResultOpen(true);
      }
      
      setImportDialogOpen(false);
      setSelectedImportGroups([]);
      setImportFile(null);
      setImportStep(1);
      setImportPreview(null);
      clearListCache();
      fetchContacts();
      fetchAvailableGroups(); // Atualizar lista de grupos disponíveis
    } catch (error) {
      toast.error('Erro ao importar contatos');
    } finally {
      setSubmitting(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Quantidade efetiva das ações em massa: todo o filtro (total) ou só os selecionados.
  const effectiveCount = selectAllMatching ? total : selectedContacts.length;
  const totalPages = Math.ceil(total / 20);

  return (
    <div className="flex flex-col h-full">
      {/* Toolbar */}
      <div className="sticky top-0 z-20 bg-background pb-4 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 pt-0 flex flex-col sm:flex-row gap-4 items-stretch sm:items-center justify-between" data-testid="table-toolbar">
        <div className="flex gap-2 flex-1">
          <div className="relative flex-1 sm:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Buscar contatos..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(0);
              }}
              className="pl-9"
              data-testid="contacts-filter-input"
            />
          </div>
          <div className="flex gap-2 items-center">
            <div className="w-48">
              <Select value={groupFilter} onValueChange={(value) => {
                setGroupFilter(value);
                setPage(0);
              }}>
                <SelectTrigger data-testid="contacts-group-filter">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Filtrar por grupo" />
                </SelectTrigger>
                <SelectContent>
                  {availableGroups.map((group) => (
                    <SelectItem key={group._id} value={group._id}>
                      {group.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {(groupFilter || search) && (
              <Button 
                variant="secondary" 
                size="sm"
                onClick={() => {
                  setGroupFilter('');
                  setSearch('');
                  setPage(0);
                }}
                className="h-10 bg-gray-200 dark:bg-gray-700 hover:bg-primary hover:text-primary-foreground"
                data-testid="clear-tag-filter"
              >
                Limpar
              </Button>
            )}
          </div>
        </div>
        {canEdit && (
          <div className="flex gap-2 items-center">
            <Button
              variant="secondary"
              disabled={selectedContacts.length === 0}
              onClick={openGroupDialog}
              className="bg-gray-200 dark:bg-gray-700 hover:bg-primary hover:text-primary-foreground disabled:opacity-50"
              data-testid="contacts-bulk-group-button"
            >
              <Users className="w-4 h-4 mr-2" />
              Grupos{effectiveCount > 0 ? ` (${effectiveCount})` : ''}
            </Button>
            <Button
              variant="secondary"
              disabled={selectedContacts.length === 0}
              onClick={() => setBulkDeleteDialogOpen(true)}
              className="bg-gray-200 dark:bg-gray-700 hover:bg-destructive hover:text-destructive-foreground disabled:opacity-50"
              data-testid="contacts-bulk-delete-button"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Excluir{effectiveCount > 0 ? ` (${effectiveCount})` : ''}
            </Button>
            <Button
              variant="secondary"
              onClick={() => { resetImportWizard(); setImportDialogOpen(true); }}
              className="bg-gray-200 dark:bg-gray-700 hover:bg-primary hover:text-primary-foreground"
              data-testid="contacts-import-csv-button"
            >
              <Upload className="w-4 h-4 mr-2" />
              Importar Excel
            </Button>
            <Button onClick={() => handleOpenDialog()} data-testid="contacts-add-button">
              <Plus className="w-4 h-4 mr-2" />
              Novo Contato
            </Button>
          </div>
        )}
      </div>

      {/* Loading State - Centralizado */}
      {loading ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="flex flex-col items-center gap-6 p-8">
            <div className="relative">
              <Loader2 className="w-16 h-16 animate-spin text-primary" />
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-sm font-semibold text-primary">
                  {Math.round(loadingPercent)}%
                </span>
              </div>
            </div>
            <p className="text-muted-foreground text-center">Carregando contatos...</p>
          </div>
        </div>
      ) : (
      <>
      {/* Banner: selecionar todos os contatos do filtro (não só a página) */}
      {contacts.length > 0 && selectedContacts.length === contacts.length && total > contacts.length && (
        <div className="mb-3 flex flex-wrap items-center justify-center gap-2 rounded-md border border-primary/30 bg-primary/5 px-4 py-2 text-sm">
          {selectAllMatching ? (
            <>
              <span>Todos os <strong>{total}</strong> contatos do filtro estão selecionados.</span>
              <button
                type="button"
                onClick={() => { setSelectAllMatching(false); setSelectedContacts([]); }}
                className="font-medium text-primary underline underline-offset-2 hover:opacity-80"
                data-testid="clear-select-all-matching"
              >
                Limpar seleção
              </button>
            </>
          ) : (
            <>
              <span>Os <strong>{contacts.length}</strong> contatos desta página estão selecionados.</span>
              <button
                type="button"
                onClick={() => setSelectAllMatching(true)}
                className="font-medium text-primary underline underline-offset-2 hover:opacity-80"
                data-testid="select-all-matching"
              >
                Selecionar todos os {total}
              </button>
            </>
          )}
        </div>
      )}
      {/* Table */}
      <Card className="flex-1 flex flex-col min-h-0 mb-4">
        <CardContent className="p-0 flex-1 flex flex-col min-h-0">
          <div className="overflow-x-auto flex-1 flex flex-col min-h-0">
            <Table data-testid="contacts-table">
            <TableHeader className="sticky top-0 bg-card z-10">
              <TableRow>
                {canEdit && (
                  <TableHead className="w-[40px]">
                    <Checkbox
                      checked={contacts.length > 0 && selectedContacts.length === contacts.length}
                      onCheckedChange={toggleSelectAll}
                      data-testid="contacts-select-all"
                    />
                  </TableHead>
                )}
                <TableHead>Nome</TableHead>
                <TableHead>Telefone</TableHead>
                <TableHead>Grupos</TableHead>
                <TableHead>Criado em</TableHead>
                {canEdit && <TableHead className="w-[100px]">Ações</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {contacts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={canEdit ? 7 : 4} className="text-center py-8 text-muted-foreground">
                    Nenhum contato encontrado
                  </TableCell>
                </TableRow>
              ) : (
                contacts.map((contact) => (
                  <TableRow key={contact._id} className={`hover:bg-primary/5 ${selectedContacts.includes(contact._id) ? 'bg-primary/10' : ''}`}>
                    {canEdit && (
                      <TableCell className="py-2">
                        <Checkbox
                          checked={selectedContacts.includes(contact._id)}
                          onCheckedChange={() => toggleSelectContact(contact._id)}
                          data-testid={`contact-select-${contact._id}`}
                        />
                      </TableCell>
                    )}
                    <TableCell className="font-medium py-2">{contact.name}</TableCell>
                    <TableCell className="py-2">{contact.phone}</TableCell>
                    <TableCell className="py-2">
                      <div className="flex flex-wrap gap-1">
                        {(contact.group_ids || []).map((groupId) => {
                          const group = availableGroups.find(g => g._id === groupId);
                          return group ? (
                            <Badge key={groupId} variant="outline" className="border-transparent shadow bg-cyan-100 text-cyan-800 dark:bg-cyan-900/40 dark:text-cyan-300">
                              {group.name}
                            </Badge>
                          ) : null;
                        })}
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground py-2">
                      {new Date(contact.created_at).toLocaleDateString('pt-BR')}
                    </TableCell>
                    {canEdit && (
                      <TableCell className="py-2">
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleOpenDialog(contact)}
                            className="h-7 w-7"
                          >
                            <Edit />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDelete(contact)}
                            className="h-7 w-7"
                          >
                            <Trash2 />
                          </Button>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          </div>
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={page === 0}
            onClick={() => setPage(page - 1)}
          >
            Anterior
          </Button>
          <span className="flex items-center px-3 text-sm text-muted-foreground">
            Página {page + 1} de {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= totalPages - 1}
            onClick={() => setPage(page + 1)}
          >
            Próxima
          </Button>
        </div>
      )}
      </>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingContact ? 'Editar Contato' : 'Novo Contato'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="name">Nome</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Telefone <span className="text-xs text-muted-foreground font-normal">(ex: +5521999999999)</span></Label>
                <div className="relative">
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => {
                      setFormData({ ...formData, phone: e.target.value });
                      setContactPhoneStatus(null);
                    }}
                    placeholder="+5521999999999"
                    required
                    data-testid="contact-phone-input"
                    className={
                      contactPhoneStatus === 'format_error' ? 'border-amber-500 pr-8' : ''
                    }
                  />
                  {contactPhoneStatus === 'format_error' && (
                    <AlertCircle className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-amber-500" />
                  )}
                </div>
                {contactPhoneStatus === 'format_error' && (
                  <p className="text-xs text-amber-600">Formato inválido. Use: +5521999999999</p>
                )}
              </div>
              <div className="space-y-2">
                <Label>Grupos</Label>
                <div className="border rounded-md p-3 space-y-2 max-h-48 overflow-y-auto">
                  {availableGroups.length === 0 ? (
                    <p className="text-sm text-muted-foreground">Nenhum grupo disponível. Crie grupos primeiro.</p>
                  ) : (
                    availableGroups.map((group) => (
                      <div key={group._id} className="flex items-center space-x-2">
                        <Checkbox
                          id={`group-${group._id}`}
                          checked={formData.group_ids.includes(group._id)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setFormData({
                                ...formData,
                                group_ids: [...formData.group_ids, group._id]
                              });
                            } else {
                              setFormData({
                                ...formData,
                                group_ids: formData.group_ids.filter(id => id !== group._id)
                              });
                            }
                          }}
                        />
                        <Label htmlFor={`group-${group._id}`} className="cursor-pointer font-normal">
                          {group.name}
                        </Label>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {editingContact ? 'Salvar' : 'Criar'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Import Wizard: 1 instruções · 2 arquivo · 3 conferir (prévia) · 4 grupos */}
      <Dialog open={importDialogOpen} onOpenChange={(open) => { setImportDialogOpen(open); if (!open) resetImportWizard(); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Importar Contatos</DialogTitle>
          </DialogHeader>

          {/* Stepper */}
          <div className="flex items-center justify-center gap-2 text-sm font-medium pb-2">
            {['Instruções', 'Arquivo', 'Conferir', 'Grupos'].map((label, i) => {
              const n = i + 1;
              const active = importStep === n;
              const done = importStep > n;
              return (
                <React.Fragment key={label}>
                  <div className={`flex items-center gap-2 ${active ? 'text-primary' : done ? 'text-teal-600' : 'text-muted-foreground'}`}>
                    <span className={`flex items-center justify-center w-7 h-7 rounded-full text-xs ${active ? 'bg-primary text-primary-foreground' : done ? 'bg-teal-600 text-white' : 'border-2 border-dashed border-muted-foreground/40 text-muted-foreground/50'}`}>
                      {done ? '✓' : n}
                    </span>
                    {label}
                  </div>
                  {n < 4 && <span className="w-6 h-px bg-border" />}
                </React.Fragment>
              );
            })}
          </div>

          <div className="py-2 min-h-[16rem]">
            {/* PASSO 1 — Instruções + download do modelo */}
            {importStep === 1 && (
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Baixe a planilha modelo, preencha no Excel e volte aqui para enviar.
                </p>
                <ol className="text-sm space-y-2 list-decimal list-inside marker:text-muted-foreground">
                  <li>Baixe o modelo no botão abaixo e abra no Excel.</li>
                  <li>Preencha uma linha para cada contato, com <strong>nome</strong> e <strong>telefone</strong>.</li>
                  <li>No telefone, use DDD e apenas números. Exemplo: 21999998888.</li>
                  <li>Apague as duas linhas de exemplo e salve mantendo o formato <strong>.xlsx</strong>.</li>
                </ol>
                <div className="flex justify-center pt-1">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={downloadTemplate}
                    disabled={templateLoading}
                    className="h-32 w-32 flex-col gap-2 whitespace-normal px-3 text-xs leading-tight [&_svg]:size-14 hover:border-primary hover:text-primary hover:bg-primary/5"
                    data-testid="import-download-template"
                  >
                    {templateLoading
                      ? <Loader2 className="animate-spin" />
                      : <img src="/excel-icon.png" alt="" aria-hidden="true" className="w-14 h-14" />}
                    <span>Baixar modelo Excel</span>
                  </Button>
                </div>
              </div>
            )}

            {/* PASSO 2 — Arquivo */}
            {importStep === 2 && (
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Envie a planilha do Excel que você preencheu, com as colunas <strong>nome</strong> e <strong>telefone</strong>.
                </p>
                <label
                  onDragOver={(e) => { e.preventDefault(); setImportDragOver(true); }}
                  onDragLeave={() => setImportDragOver(false)}
                  onDrop={(e) => { e.preventDefault(); setImportDragOver(false); onImportFileChosen(e.dataTransfer.files?.[0]); }}
                  className={`flex flex-col items-center justify-center gap-2 border-2 border-dashed rounded-lg p-8 cursor-pointer transition-colors ${importDragOver ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'}`}
                >
                  <Upload className="w-8 h-8 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground text-center">
                    Arraste o arquivo aqui ou <span className="text-primary font-medium">clique para escolher</span>
                  </span>
                  <span className="text-xs text-muted-foreground">.xlsx</span>
                  <input
                    type="file"
                    accept=".xlsx,.csv,.txt"
                    ref={fileInputRef}
                    className="hidden"
                    onChange={(e) => onImportFileChosen(e.target.files?.[0])}
                  />
                </label>
                {previewLoading && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="w-4 h-4 animate-spin" /> Lendo o arquivo...
                  </div>
                )}
              </div>
            )}

            {/* PASSO 3 — Conferir (prévia) */}
            {importStep === 3 && importPreview && (
              <div className="space-y-3">
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <span className="text-teal-600 font-medium">{importPreview.imported} novos</span>
                  <span className="text-muted-foreground">·</span>
                  <span className="text-amber-600">{importPreview.skipped} já cadastrados</span>
                  <span className="text-muted-foreground">·</span>
                  <span className="text-red-600">{importPreview.invalid || 0} com problema</span>
                </div>
                <div className="border rounded-md max-h-64 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-gray-50 dark:bg-gray-800">
                      <tr>
                        <th className="text-left font-medium px-3 py-1.5">Nome</th>
                        <th className="text-left font-medium px-3 py-1.5">Telefone</th>
                        <th className="text-left font-medium px-3 py-1.5">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(importPreview.preview_rows || []).map((row, i) => (
                        <tr key={i} className="border-t">
                          <td className="px-3 py-1.5">{row.name || <span className="text-muted-foreground italic">sem nome</span>}</td>
                          <td className="px-3 py-1.5 font-mono text-xs">{row.phone || <span className="font-sans text-muted-foreground italic">vazio</span>}</td>
                          <td className="px-3 py-1.5 whitespace-nowrap">
                            {row.status === 'new' && <span className="text-teal-600">✓ novo</span>}
                            {row.status === 'exists' && <span className="text-amber-600">• já existe</span>}
                            {row.status === 'invalid' && <span className="text-red-600" title={row.reason}>⚠ {row.reason || 'inválido'}</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {importPreview.total_processed > (importPreview.preview_rows?.length || 0) && (
                  <p className="text-xs text-muted-foreground">
                    Mostrando as primeiras {importPreview.preview_rows.length} de {importPreview.total_processed} linhas.
                  </p>
                )}
                {importPreview.imported === 0 && (
                  <p className="text-sm text-amber-600">Nenhum contato novo para importar neste arquivo.</p>
                )}
              </div>
            )}

            {/* PASSO 4 — Grupos */}
            {importStep === 4 && (
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Vão ser importados <strong className="text-teal-600">{importPreview?.imported || 0}</strong> contatos novos. Atribuir a algum grupo? <span className="text-xs">(opcional)</span>
                </p>
                <div className="border rounded-md p-3 space-y-2 max-h-48 overflow-y-auto">
                  {availableGroups.length === 0 ? (
                    <p className="text-sm text-muted-foreground">Nenhum grupo disponível. Crie grupos primeiro.</p>
                  ) : (
                    availableGroups.map((group) => (
                      <div key={group._id} className="flex items-center space-x-2">
                        <Checkbox
                          id={`import-group-${group._id}`}
                          checked={selectedImportGroups.includes(group._id)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setSelectedImportGroups([...selectedImportGroups, group._id]);
                            } else {
                              setSelectedImportGroups(selectedImportGroups.filter(id => id !== group._id));
                            }
                          }}
                        />
                        <Label htmlFor={`import-group-${group._id}`} className="cursor-pointer font-normal">
                          {group.name}
                        </Label>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            {importStep === 1 && (
              <>
                <Button variant="outline" onClick={() => { setImportDialogOpen(false); resetImportWizard(); }}>
                  Cancelar
                </Button>
                <Button onClick={() => setImportStep(2)} data-testid="import-step1-next">
                  Já preenchi, enviar arquivo
                </Button>
              </>
            )}
            {importStep === 2 && (
              <>
                <Button variant="outline" onClick={() => setImportStep(1)}>
                  Voltar
                </Button>
              </>
            )}
            {importStep === 3 && (
              <>
                <Button variant="outline" onClick={() => { setImportStep(2); setImportFile(null); setImportPreview(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}>
                  Voltar
                </Button>
                <Button onClick={() => setImportStep(4)} disabled={(importPreview?.imported || 0) === 0}>
                  Próximo
                </Button>
              </>
            )}
            {importStep === 4 && (
              <>
                <Button variant="outline" onClick={() => setImportStep(3)}>
                  Voltar
                </Button>
                <Button onClick={handleImport} disabled={submitting} data-testid="contacts-import-submit">
                  {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                  Importar {importPreview?.imported || 0} contato(s)
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Result Modal */}
      <Dialog open={importResultOpen} onOpenChange={setImportResultOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Resultado da Importação</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            {importResult && (
              <>
                {importResult.skipped > 0 && (
                  <div className="space-y-3">
                    <p className="font-medium text-orange-600">
                      {importResult.skipped} contato(s) não foram importados
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Os telefones abaixo já estão cadastrados:
                    </p>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {importResult.skipped_contacts?.map((contact, index) => (
                        <div key={index} className="p-2 bg-orange-50 rounded-lg border">
                          <p className="text-sm font-medium">{contact.name}</p>
                          <p className="text-xs text-muted-foreground">{contact.phone}</p>
                          <p className="text-xs text-orange-600">
                            Já existe como: "{contact.existing_name}"
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {importResult.imported === 0 && importResult.skipped === 0 && importResult.errors?.length === 0 && (
                  <div className="text-center py-4">
                    <p className="text-muted-foreground">
                      Nenhum contato foi encontrado no arquivo
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
          <DialogFooter>
            <Button onClick={() => setImportResultOpen(false)} className="w-full">
              OK
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Error Modal */}
      <Dialog open={errorModalOpen} onOpenChange={setErrorModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600">Erro ao Criar Contato</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <div className="p-4 bg-red-50 rounded-lg border border-red-200">
              <p className="text-sm text-red-800">{errorMessage}</p>
            </div>
          </div>
          <DialogFooter>
            <Button 
              onClick={() => setErrorModalOpen(false)} 
              className="w-full"
              variant="destructive"
            >
              OK
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmar Exclusão</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-muted-foreground">
              Tem certeza que deseja excluir o contato <strong>{contactToDelete?.name}</strong>?
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={confirmDelete}>
              Excluir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Delete Confirmation Dialog */}
      <Dialog open={bulkDeleteDialogOpen} onOpenChange={setBulkDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmar Exclusão em Massa</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-muted-foreground">
              Tem certeza que deseja excluir <strong>{effectiveCount} contato(s)</strong>? Esta ação não pode ser desfeita.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkDeleteDialogOpen(false)}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={confirmBulkDelete} disabled={bulkDeleting} data-testid="contacts-bulk-delete-confirm">
              {bulkDeleting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Excluir {effectiveCount} contato(s)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Group Assign Dialog */}
      <Dialog open={groupDialogOpen} onOpenChange={setGroupDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Grupos de {effectiveCount} contato(s)</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            {/* Alternar entre adicionar / remover */}
            <div className="flex gap-2">
              <Button
                type="button"
                variant={groupAction === 'add' ? 'default' : 'secondary'}
                size="sm"
                className="flex-1"
                onClick={() => changeGroupAction('add')}
                data-testid="group-action-add"
              >
                Adicionar a grupo
              </Button>
              <Button
                type="button"
                variant={groupAction === 'remove' ? 'default' : 'secondary'}
                size="sm"
                className="flex-1"
                onClick={() => changeGroupAction('remove')}
                data-testid="group-action-remove"
              >
                Remover de grupo
              </Button>
            </div>

            <p className="text-xs text-muted-foreground min-h-8">
              {groupAction === 'add'
                ? 'Os grupos marcados serão adicionados aos contatos selecionados (os grupos que eles já têm são mantidos).'
                : 'Os grupos marcados serão removidos dos contatos selecionados.'}
              {selectAllMatching && ' Vários contatos selecionados: mostrando todos os grupos.'}
            </p>

            {/* Lista de grupos (filtrada pela pertinência dos contatos selecionados) */}
            <div className="border rounded-md p-3 space-y-2 max-h-48 overflow-y-auto">
              {(() => {
                if (availableGroups.length === 0) {
                  return <p className="text-sm text-muted-foreground">Nenhum grupo disponível. Crie um abaixo.</p>;
                }
                const dialogGroups = groupsForDialog();
                if (dialogGroups.length === 0) {
                  return (
                    <p className="text-sm text-muted-foreground">
                      {groupAction === 'add'
                        ? 'Os contatos selecionados já estão em todos os grupos.'
                        : 'Os contatos selecionados não estão em nenhum grupo.'}
                    </p>
                  );
                }
                return dialogGroups.map((group) => (
                  <div key={group._id} className="flex items-center space-x-2">
                    <Checkbox
                      id={`bulk-group-${group._id}`}
                      checked={groupDialogSelected.includes(group._id)}
                      onCheckedChange={() => toggleGroupDialogSelected(group._id)}
                    />
                    <Label htmlFor={`bulk-group-${group._id}`} className="cursor-pointer font-normal">
                      {group.name}
                    </Label>
                  </div>
                ));
              })()}
            </div>

            {/* Criar grupo novo (só ao adicionar) — altura reservada p/ o modal não pular ao trocar de aba */}
            <div className="min-h-10">
              {groupAction === 'add' && (
                <div className="flex gap-2">
                  <Input
                    placeholder="Criar grupo novo..."
                    value={newGroupName}
                    onChange={(e) => setNewGroupName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleCreateGroupInline();
                      }
                    }}
                    data-testid="group-create-inline-input"
                  />
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={handleCreateGroupInline}
                    disabled={!newGroupName.trim() || creatingGroup}
                    data-testid="group-create-inline-button"
                  >
                    {creatingGroup ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                  </Button>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setGroupDialogOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={applyGroups}
              disabled={applyingGroups || groupDialogSelected.length === 0}
              variant={groupAction === 'remove' ? 'destructive' : 'default'}
              data-testid="group-apply-button"
            >
              {applyingGroups && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {groupAction === 'add' ? 'Adicionar' : 'Remover'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
